import { useRef, useState } from "react";
import { toast } from "sonner";
import { Download, FileSpreadsheet, FileText, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API, authFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Admin-only CSV / PDF import + export toolbar for a master-data resource.
 *
 *   <DataIOToolbar resource="suppliers" label="suppliers" onImported={load} />
 *
 * Renders three actions:
 *   - Export CSV
 *   - Export PDF
 *   - Import CSV  (and a "Template" sub-link)
 *
 * Hidden entirely for non-admin users.
 */
export default function DataIOToolbar({ resource, label, onImported }) {
  const { user } = useAuth();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(null); // "csv" | "pdf" | "tpl" | "import" | null

  if (user?.role !== "admin") return null;

  const download = async (suffix, mime, ext, key) => {
    setBusy(key);
    try {
      const r = await authFetch(`${API}/admin/export/${resource}${suffix}`);
      if (!r.ok) throw new Error(`Download failed (${r.status})`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${resource}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message || "Download failed");
    } finally {
      setBusy(null);
    }
  };

  const downloadTemplate = async () => {
    setBusy("tpl");
    try {
      const r = await authFetch(
        `${API}/admin/import/${resource}/template.csv`
      );
      if (!r.ok) throw new Error("Template download failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${resource}-template.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy("import");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await authFetch(`${API}/admin/import/${resource}`, {
        method: "POST",
        body: fd,
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        throw new Error(data?.detail || `Import failed (${r.status})`);
      }
      const bits = [`${data.inserted} added`];
      if (data.duplicates?.length) bits.push(`${data.duplicates.length} duplicate`);
      if (data.errors?.length) bits.push(`${data.errors.length} error`);
      toast.success(`${label} imported · ${bits.join(" · ")}`);
      onImported?.();
    } catch (err) {
      toast.error(err.message || "Import failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`io-toolbar-${resource}`}>
      <Button
        variant="outline"
        size="sm"
        disabled={busy === "csv"}
        onClick={() => download(".csv", "text/csv", "csv", "csv")}
        data-testid={`export-csv-${resource}`}
        title="Download all rows as CSV"
      >
        <FileSpreadsheet className="h-4 w-4 mr-1.5" /> CSV
      </Button>
      <Button
        variant="outline"
        size="sm"
        disabled={busy === "pdf"}
        onClick={() => download(".pdf", "application/pdf", "pdf", "pdf")}
        data-testid={`export-pdf-${resource}`}
        title="Download all rows as printable PDF"
      >
        <FileText className="h-4 w-4 mr-1.5" /> PDF
      </Button>
      <input
        ref={fileRef}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        onChange={onPick}
        data-testid={`import-input-${resource}`}
      />
      <Button
        variant="outline"
        size="sm"
        disabled={busy === "import"}
        onClick={() => fileRef.current?.click()}
        data-testid={`import-csv-${resource}`}
        title="Upload a CSV to bulk-add rows (duplicates by name are skipped)"
      >
        <Upload className="h-4 w-4 mr-1.5" /> Import CSV
      </Button>
      <button
        type="button"
        className="text-xs text-stone-500 hover:text-stone-800 underline-offset-2 hover:underline px-1"
        disabled={busy === "tpl"}
        onClick={downloadTemplate}
        data-testid={`import-template-${resource}`}
      >
        <Download className="h-3 w-3 inline mr-1" />
        template
      </button>
    </div>
  );
}
